import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Wrench, Star, CheckCircle, ArrowRight } from 'lucide-react';

const AboutPage = () => {
  const pillars = [
    {
      icon: Zap,
      title: 'Swift',
      description: 'Rapid response and timely completion to minimize disruption.',
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      icon: Wrench,
      title: 'Fix',
      description: 'Lasting, effective solutions handled by skilled professionals.',
      gradient: 'from-orange-500 to-orange-600'
    },
    {
      icon: Star,
      title: 'Pro',
      description: 'Professional standards, transparent pricing, and reliable service.',
      gradient: 'from-purple-500 to-purple-600'
    }
  ];

  const benefits = [
    'One-stop solution covering all essential repair, maintenance, and improvement needs',
    'Certified and experienced technicians across multiple service areas',
    'Affordable, transparent pricing with no hidden costs',
    'Flexible scheduling and prompt response',
    'Strong commitment to service excellence and customer satisfaction'
  ];


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Modern Hero Section - Matching HomePage Style */}
      <section className="relative overflow-hidden">
        {/* Background with geometric patterns */}
        <div className="absolute inset-0 bg-orange-600">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700"></div>
          {/* Geometric shapes */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500 rounded-full opacity-20 transform translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-orange-700 rounded-full opacity-30 transform -translate-x-24 translate-y-24"></div>
          <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-white opacity-5 rounded-full"></div>
          <div className="absolute top-1/4 right-1/3 w-48 h-48 bg-orange-400 rounded-full opacity-10 transform rotate-45"></div>
        </div>

        <div className="relative container mx-auto px-4 py-24">
          <div className="max-w-4xl mx-auto text-center text-white">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <span className="inline-block px-4 py-2 bg-orange-500 bg-opacity-30 rounded-full text-orange-100 text-sm font-medium mb-4 backdrop-blur-sm">
                About Our Company
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
            >
              About
              <span className="block text-orange-200">Swift Fix Pro</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-xl text-orange-100 max-w-3xl mx-auto mb-6 leading-relaxed"
            >
              Swiftfixpro Pte Ltd is a trusted solutions provider specializing in fast, reliable, and professional repair, maintenance, and technical services for both residential and commercial clients.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-lg text-orange-100 max-w-3xl mx-auto mb-10 leading-relaxed"
            >
              Established in Singapore with a mission to simplify and provide essential repair and maintenance work at affordable costs while delivering speed, efficiency, and excellence without compromise.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <a
                href="#story"
                className="bg-white text-orange-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center gap-2 text-lg"
              >
                Our Story
                <ArrowRight size={20} />
              </a>
              <a
                href="/contact"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-orange-600 transition-colors text-lg"
              >
                Contact Us
              </a>
            </motion.div>
          </div>
        </div>
      </section>


      {/* Our Story Section - Three Pillars */}
      <section id="story" className="py-24 bg-white relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-orange-50 to-transparent rounded-full opacity-50"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-orange-50 to-transparent rounded-full opacity-40"></div>
        <div className="absolute top-1/3 right-1/4 w-4 h-4 bg-orange-200 rounded-full opacity-50"></div>
        <div className="absolute bottom-1/3 left-1/3 w-3 h-3 bg-orange-300 rounded-full opacity-40"></div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="relative inline-block">
              <span className="inline-block px-5 py-2 bg-orange-100 rounded-full text-orange-600 text-sm font-semibold mb-4 uppercase tracking-wide">
                Our Philosophy
              </span>
              {/* Small decorative dots around badge */}
              <div className="absolute -top-1 -left-1 w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
              <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-orange-400 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 mt-4">Our Story</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              True to our name—<span className="font-semibold text-gray-900">Swift, Fix, Pro</span>—our service approach is built on three core pillars
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {pillars.map((pillar, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group"
              >
                <div className="bg-white border border-gray-200 rounded-2xl p-8 h-full hover:shadow-2xl hover:border-orange-200 transition-all duration-300 relative overflow-hidden">
                  {/* Decorative corner pattern */}
                  <div className="absolute top-0 right-0 w-20 h-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-bl from-orange-100 to-transparent rounded-bl-3xl"></div>
                  </div>

                  <div className="relative z-10">
                    <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${pillar.gradient} text-white rounded-xl mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                      <pillar.icon size={28} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{pillar.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{pillar.description}</p>

                    {/* Bottom accent line */}
                    <div className="mt-6 h-1 bg-gradient-to-r from-orange-400 to-transparent rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-gradient-to-br from-gray-50 to-orange-50/30 rounded-2xl p-8 md:p-12 border border-gray-200">
              <p className="text-lg text-gray-700 leading-relaxed text-center">
                Through <span className="font-semibold text-gray-900">digital booking systems</span>, <span className="font-semibold text-gray-900">clear cost structures</span>, and <span className="font-semibold text-gray-900">customer-centric operations</span>, Swiftfixpro ensures a seamless and worry-free experience for every client.
              </p>
              <div className="mt-8 pt-8 border-t border-gray-200">
                <p className="text-gray-600 leading-relaxed text-center italic">
                  With a customer-first philosophy and a team of skilled technicians across multiple trades, Swiftfixpro is committed to ensuring that homes, offices, and facilities remain safe, functional, and comfortable.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-gray-50 relative overflow-hidden">
        {/* Decorative background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgb(249 115 22) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="absolute top-20 left-10 w-32 h-32 bg-orange-200 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-orange-100 rounded-full opacity-30 blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="relative inline-block mb-4">
              <span className="inline-block px-5 py-2 bg-orange-100 rounded-full text-orange-600 text-sm font-semibold uppercase tracking-wide relative">
                Our Team
                {/* Decorative underline */}
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-orange-400 rounded-full"></div>
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 mt-6">Why Choose Swiftfixpro Pte Ltd?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the difference with our comprehensive service offering and commitment to excellence
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group"
                >
                  <div className="flex items-start gap-4 bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg hover:border-orange-300 transition-all duration-300 relative overflow-hidden">
                    {/* Decorative gradient on hover */}
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    <div className="flex-shrink-0 mt-1 relative z-10">
                      <div className="relative">
                        <CheckCircle className="text-orange-500 group-hover:scale-110 transition-transform duration-300" size={24} />
                        {/* Glow effect on hover */}
                        <div className="absolute inset-0 bg-orange-400 rounded-full blur-md opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
                      </div>
                    </div>
                    <p className="text-gray-700 leading-relaxed relative z-10">{benefit}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default AboutPage; 